import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Utilities } from "@tylertech/shared-module";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { RepositoryService } from "@tylertech/core-module";
import
{
    LgdFormControl,
    LgdFormControlFactory,
    LgdFormControlTextConfig,
    LgdTypeAheadComponent,
    setEnabled,
} from "@tylertech/lgd-reactive-forms";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { VendorLookupService } from "@tylertech/accounts-payable-module";
import
{
    IClassCode,
    IItem,
    IItemLocation,
    IItemVendor,
    IStatusCode,
    IUnit,
    IVendorLookup,
    LocationCodeViewModel,
    ExecutionResult,
} from "@tylertech/tyler-api-types";
import
{
    ItemClassLookupService,
    ItemService,
    ItemStatusLookupService,
    LocationCodeLookupService,
    UnitLookupService,
} from "../services";
import { HttpErrorResponse } from "@angular/common/http";
@Component({
    selector: "add-item",
    templateUrl: "./addItem.tmpl.html",
    styleUrls: ["./addItem.component.scss"],
    standalone: false
})
export class AddItemComponent implements OnInit, OnDestroy
{
    private readonly dialogRef = inject<MatDialogRef<unknown, IItem | undefined>>(MatDialogRef);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $itemService = inject(ItemService);
    private readonly $inventoryLocationService = inject(LocationCodeLookupService);
    private readonly $vendorLookupService = inject(VendorLookupService);
    private readonly $unitLookupService = inject(UnitLookupService);
    private readonly $itemClassLookupService = inject(ItemClassLookupService);
    private readonly $itemStatusLookupService = inject(ItemStatusLookupService);
    private readonly $repositoryService = inject(RepositoryService);

    private readonly destructor$ = new Subject<void>();
    private readonly classSubject = new BehaviorSubject<Array<IClassCode>>([]);
    private readonly locationStatusSubject = new BehaviorSubject<Array<IStatusCode>>([]);
    private readonly statusesSubject = new BehaviorSubject<Array<IStatusCode>>([]);
    private readonly locationSubject = new BehaviorSubject<Array<LocationCodeViewModel>>([]);
    private readonly stockLocationSubject = new BehaviorSubject<Array<LocationCodeViewModel>>([]);
    private readonly unitSubject = new BehaviorSubject<Array<IUnit>>([]);
    private readonly vendorSubject = new BehaviorSubject<Array<IVendorLookup>>([]);
    readonly addItemForm: FormGroup;
    readonly commands: Array<Command>;
    @Input()
    description: string;

    @ViewChild("classLookup")
    private readonly classLgdTypeahead: LgdTypeAheadComponent<IClassCode>;

    @ViewChild("unitLookup")
    private readonly unitLgdTypeahead: LgdTypeAheadComponent<IUnit>;

    @ViewChild("stockLocationLookup")
    private readonly stockLocationLgdTypeahead: LgdTypeAheadComponent<LocationCodeViewModel>;

    private _locationStatus: IStatusCode;
    private _newStockLocation: boolean;
    private _selectedLocation: LocationCodeViewModel;
    private inventoryModuleControl: Record<string, unknown>;
    private item: IItem;
    private part: string;
    private price: number;
    private selectedStockLocation: LocationCodeViewModel;
    private selectedVendor: IVendorLookup;
    processing: boolean;
    get newStockLocation(): boolean
    {
        return this._newStockLocation;
    }

    set newStockLocation(value: boolean)
    {
        this._newStockLocation = value;
        setEnabled(this.stockLocationControl, !value);
    }

    AddClass: boolean;
    AddUnit: boolean;
    constructor()
    {
        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.close(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Save",
                action: () => this.save(),
                getEnabled: () =>
                {
                    return (
                        this.addItemForm.valid &&
                        // TODO(ET) is it possible to for the form to be valid and the 0 < ConversionFactor <= 1 is not true?
                        (this.AddUnit ? this.isConversionFactorValid : true)
                    );
                },
            }),
        ];
        this.addItemForm = new FormGroup({
            number: LgdFormControlFactory.text(undefined, true, {}),
            status: LgdFormControlFactory.select<IStatusCode>(undefined, true, {
                displayValueFn: x => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            description: LgdFormControlFactory.text(undefined, true, {}),
            detailedDescription: LgdFormControlFactory.text(undefined, false, {}),
            location: LgdFormControlFactory.typeahead<LocationCodeViewModel>(undefined, true, {
                displayValueFn: x => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            stockLocation: LgdFormControlFactory.typeahead<LocationCodeViewModel>(undefined, false, {
                displayValueFn: x => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            locationCode: LgdFormControlFactory.text(undefined, () => this.newStockLocation, {
                maxLength: 20,
            }),
            locationDescription: LgdFormControlFactory.text(undefined, () => this.newStockLocation, {
                maxLength: 50,
            }),
            locationStatus: LgdFormControlFactory.select<IStatusCode>(undefined, true, {
                displayValueFn: x => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            class: LgdFormControlFactory.typeahead<IClassCode>(undefined, () => true, {
                displayValueFn: x => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            classCode: LgdFormControlFactory.text(undefined, () => this.AddClass, { maxLength: 20 }),
            classDescription: LgdFormControlFactory.text(undefined, () => this.AddClass, {
                maxLength: 50,
            }),
            reorder: LgdFormControlFactory.number(undefined, true, {
                min: 0,
            }),
            unit: LgdFormControlFactory.typeahead<IUnit>(undefined, () => !this.AddUnit, {
                displayValueFn: x => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            unitCode: LgdFormControlFactory.text(undefined, () => this.AddUnit, {
                maxLength: 20,
            }),
            unitDescription: LgdFormControlFactory.text(undefined, () => this.AddUnit, {
                maxLength: 50,
            }),
            conversionFactor: LgdFormControlFactory.number(undefined, () => this.AddUnit, {
                min: 1e-8,
                max: 1,
                scale: 8,
            }),
            vendor: LgdFormControlFactory.typeahead<IVendorLookup>(undefined, true, {
                displayValueFn: x => (x ? `${x.Number} - ${x.Name}` : ""),
            }),
            partNumber: LgdFormControlFactory.text(undefined, false, {}),
            price: LgdFormControlFactory.currency(undefined, false, {
                min: 0,
                scale: 6,
            }),
        });
    }

    private get numberControl(): AbstractControl<string>
    {
        return this.addItemForm.get("number");
    }

    private get statusControl(): AbstractControl<IStatusCode>
    {
        return this.addItemForm.get("status");
    }

    private get descriptionControl(): AbstractControl<string>
    {
        return this.addItemForm.get("description");
    }

    private get detailedDescriptionControl(): AbstractControl<string>
    {
        return this.addItemForm.get("detailedDescription");
    }

    private get locationControl(): AbstractControl<LocationCodeViewModel>
    {
        return this.addItemForm.get("location");
    }

    private get stockLocationControl(): AbstractControl<LocationCodeViewModel>
    {
        return this.addItemForm.get("stockLocation");
    }

    private get locationCodeControl(): LgdFormControl<LgdFormControlTextConfig, string>
    {
        return this.addItemForm.get("locationCode") as LgdFormControl<LgdFormControlTextConfig, string>;
    }

    private get locationDescriptionControl(): AbstractControl<string>
    {
        return this.addItemForm.get("locationDescription");
    }

    private get locationStatusControl(): AbstractControl<IStatusCode>
    {
        return this.addItemForm.get("locationStatus");
    }

    private get classControl(): AbstractControl<IClassCode>
    {
        return this.addItemForm.get("class");
    }

    private get classCodeControl(): AbstractControl<string>
    {
        return this.addItemForm.get("classCode");
    }

    private get classDescriptionControl(): AbstractControl<string>
    {
        return this.addItemForm.get("classDescription");
    }

    private get reorderControl(): AbstractControl<number>
    {
        return this.addItemForm.get("reorder");
    }

    private get unitControl(): AbstractControl<IUnit>
    {
        return this.addItemForm.get("unit");
    }

    private get unitCodeControl(): AbstractControl<string>
    {
        return this.addItemForm.get("unitCode");
    }

    private get unitDescriptionControl(): AbstractControl<string>
    {
        return this.addItemForm.get("unitDescription");
    }

    private get conversionFactorControl(): AbstractControl<number>
    {
        return this.addItemForm.get("conversionFactor");
    }

    private get vendorControl(): AbstractControl<IVendorLookup>
    {
        return this.addItemForm.get("vendor");
    }

    private get partNumberControl(): AbstractControl<string>
    {
        return this.addItemForm.get("partNumber");
    }

    private get priceControl(): AbstractControl<number>
    {
        return this.addItemForm.get("price");
    }

    private get isConversionFactorValid(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.item?.Unit?.ConversionFactor))
        {
            return false;
        }
        return (
            this.item.Unit.ConversionFactor &&
            0 < this.item.Unit.ConversionFactor &&
            this.item.Unit.ConversionFactor <= 1
        );
    }

    get statusesObservable(): Observable<Array<IStatusCode>>
    {
        return this.statusesSubject.asObservable();
    }

    get locationsObservable(): Observable<Array<LocationCodeViewModel>>
    {
        return this.locationSubject.asObservable();
    }

    get stockLocationsObservable(): Observable<Array<LocationCodeViewModel>>
    {
        return this.stockLocationSubject.asObservable();
    }

    get locationStatusesObservable(): Observable<Array<IStatusCode>>
    {
        return this.locationStatusSubject.asObservable();
    }

    get classesObservable(): Observable<Array<IClassCode>>
    {
        return this.classSubject.asObservable();
    }

    get unitsObservable(): Observable<Array<IUnit>>
    {
        return this.unitSubject.asObservable();
    }

    get vendorsObservable(): Observable<Array<IVendorLookup>>
    {
        return this.vendorSubject.asObservable();
    }

    get conversionString(): string
    {
        if (this.isConversionFactorValid)
        {
            const units = 1 / this.item.Unit.ConversionFactor;
            return `1 Item = ${units} Unit(s)`;
        }
        return "";
    }

    get disableStockLocation(): boolean
    {
        return !this._selectedLocation;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.item = {
            Description: this.description,
            ReOrder: 0,
            IsNew: true,
            IsDeleted: false,
        } as IItem;
        Promise.all([
            this.fetchModuleControl(),
            this.loadSelects(),
            this.generateNewNumber().then(() =>
            {
                this.numberControl.setValue(this.item.Number, { emitEvent: false });
                this.statusControl.setValue(this.item.Status, { emitEvent: false });
                this.descriptionControl.setValue(this.item.Description, { emitEvent: false });
                this.detailedDescriptionControl.setValue(this.item.DetailedDescription, { emitEvent: false });
                this.locationControl.setValue(this._selectedLocation, { emitEvent: false });
                this.stockLocationControl.setValue(this.selectedStockLocation, { emitEvent: false });
                this.locationCodeControl.setValue(this.selectedStockLocation?.Code, { emitEvent: false });
                this.locationDescriptionControl.setValue(this.selectedStockLocation?.Description, { emitEvent: false });
                this.locationStatusControl.setValue(this._locationStatus, { emitEvent: false });
                this.classControl.setValue(this.item.Class, { emitEvent: false });
                this.classCodeControl.setValue(this.item.Class?.Code, { emitEvent: false });
                this.classDescriptionControl.setValue(this.item.Class?.Description, { emitEvent: false });
                this.reorderControl.setValue(this.item.ReOrder, { emitEvent: false });
                this.unitControl.setValue(this.item.Unit, { emitEvent: false });
                this.unitCodeControl.setValue(this.item.Unit?.Code, { emitEvent: false });
                this.unitDescriptionControl.setValue(this.item.Unit?.Description, { emitEvent: false });
                this.conversionFactorControl.setValue(this.item.Unit?.ConversionFactor, { emitEvent: false });
                this.vendorControl.setValue(this.selectedVendor, { emitEvent: false });
                this.partNumberControl.setValue(this.part, { emitEvent: false });
                this.priceControl.setValue(this.price, { emitEvent: false });
            }),
        ]);
        subscribeToValueChanges(this.numberControl, (newValue: string) =>
        {
            this.item.Number = newValue;
        });
        subscribeToValueChanges(this.statusControl, (newValue: IStatusCode) =>
        {
            this.item.Status = newValue;
        });
        subscribeToValueChanges(this.descriptionControl, (newValue: string) =>
        {
            this.item.Description = newValue;
        });
        subscribeToValueChanges(this.detailedDescriptionControl, (newValue: string) =>
        {
            this.item.DetailedDescription = newValue;
        });
        subscribeToValueChanges(this.locationControl, (newValue: LocationCodeViewModel) =>
        {
            this._selectedLocation = newValue;
            if (!this._selectedLocation)
            {
                this.ClearStockLocation();
            }
        });
        subscribeToValueChanges(this.stockLocationControl, (newValue: LocationCodeViewModel) =>
        {
            this.selectedStockLocation = newValue;
        });
        subscribeToValueChanges(this.locationCodeControl, (newValue: string) =>
        {
            if (!this.selectedStockLocation) return;
            this.selectedStockLocation.Code = newValue ? newValue.substring(0, 20) : "";
        });
        subscribeToValueChanges(this.locationDescriptionControl, (newValue: string) =>
        {
            if (!this.selectedStockLocation) return;
            this.selectedStockLocation.Description = newValue ? newValue.substring(0, 50) : "";
        });
        subscribeToValueChanges(this.locationStatusControl, (newValue: LocationCodeViewModel) =>
        {
            this._locationStatus = newValue;
        });
        subscribeToValueChanges(this.classControl, (newValue: IClassCode) =>
        {
            this.item.Class = newValue;
        });
        subscribeToValueChanges(this.classCodeControl, (newValue: string) =>
        {
            this.item.Class.Code = newValue;
        });
        subscribeToValueChanges(this.classDescriptionControl, (newValue: string) =>
        {
            this.item.Class.Description = newValue;
        });
        subscribeToValueChanges(this.reorderControl, (newValue: number) =>
        {
            this.item.ReOrder = newValue;
        });
        subscribeToValueChanges(this.unitControl, (newValue: IUnit) =>
        {
            this.item.Unit = newValue;
        });
        subscribeToValueChanges(this.unitCodeControl, (newValue: string) =>
        {
            this.item.Unit.Code = newValue;
        });
        subscribeToValueChanges(this.unitDescriptionControl, (newValue: string) =>
        {
            this.item.Unit.Description = newValue;
        });
        subscribeToValueChanges(this.conversionFactorControl, (newValue: number) =>
        {
            this.item.Unit.ConversionFactor = newValue;
        });
        subscribeToValueChanges(this.vendorControl, (newValue: IVendorLookup) =>
        {
            this.selectedVendor = newValue;
        });
        subscribeToValueChanges(this.partNumberControl, (newValue: string) =>
        {
            this.part = newValue;
        });
        subscribeToValueChanges(this.priceControl, (newValue: number) =>
        {
            this.price = newValue;
        });
        function subscribeToValueChanges<T = unknown>(
            control: AbstractControl<T>,
            subscriptionFn: (newValue: T) => void
        ): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    private ClearStockLocation(): void
    {
        this.newStockLocation = false;
        this.stockLocationControl.setValue(undefined);
        this.stockLocationLgdTypeahead.clearSearchText();
        this.locationCodeControl.setValue(null);
        this.locationDescriptionControl.setValue(null);
    }

    private fetchModuleControl(): Promise<void>
    {
        return this.$repositoryService
            .Fetch("TylerTech.Incode.Inventory.Model.ModuleControl")
            .then((result: Array<Record<string, unknown>>) =>
            {
                this.inventoryModuleControl = result[0];
                if (this.inventoryModuleControl?.DefaultUnitOfMeasureId)
                {
                    return this.fetchDefaultUnitOfMeasure();
                }
            });
    }

    private fetchDefaultUnitOfMeasure(): Promise<void>
    {
        return this.$unitLookupService
            .Lookup({
                Parameters: [
                    {
                        FieldId: "Id",
                        Value: this.inventoryModuleControl.DefaultUnitOfMeasureId,
                    },
                ],
            })
            .then((result: Array<IUnit>) =>
            {
                this.unitControl.setValue(result[0]);
                this.unitDescriptionControl.setValue(`${result[0].Code} - ${result[0].Description}`.substring(0, 50));
                this.unitCodeControl.setValue(`${result[0].Code} - ${result[0].Description}`.substring(0, 20));
            });
    }

    private loadSelects(): Promise<void>
    {
        return this.$itemStatusLookupService.Lookup({}).then((result: Array<IStatusCode>) =>
        {
            this.statusesSubject.next(result);
        });
    }

    private generateNewNumber(): Promise<void>
    {
        return this.$itemService.GetNextItemNumber().then(
            (result: string) =>
            {
                this.item.Number = result;
            },
            (_err: HttpErrorResponse) =>
            {
                this.$commonDialogsService.showAlertMessage(
                    "You do not have permission to add Inventory Items.",
                    "No Permission"
                );
            }
        );
    }

    // TODO(ET) - Custom validators?
    // /** @deprecated */
    // private activateErrorMessages(): void
    // {
    //     this.errorMessages = [new ErrorMessageImpl("Class.Unknown", "This class does not exist, please add one"),
    //     new ErrorMessageImpl("StockLocation.Unknown", "This location does not exist, please add one"),
    //     new ErrorMessageImpl("Unit.Unknown", "This unit does not exist, please add it")];
    // }
    addNewClass(): void
    {
        this.AddClass = !this.AddClass;
        if (this.AddClass)
        {
            const value = this.classLgdTypeahead?.searchText;
            this.item.Class = <IClassCode>{};
            this.classDescriptionControl.setValue(value ? value.substring(0, 50) : "");
            this.classCodeControl.setValue(value ? value.substring(0, 20) : "");
            setEnabled(this.classControl, false);
        }
        else
        {
            this.classControl.setValue(undefined);
            this.classLgdTypeahead?.clearSearchText();
            setEnabled(this.classControl, true);
        }
    }

    addNewUnit(): void
    {
        this.AddUnit = !this.AddUnit;
        if (this.AddUnit)
        {
            if (this.unitLgdTypeahead?.searchText !== "")
            {
                const value = this.unitLgdTypeahead?.searchText;
                this.item.Unit = <IUnit>{};
                this.conversionFactorControl.setValue(0);
                this.unitDescriptionControl.setValue(value ? value.substring(0, 50) : "");
                this.unitCodeControl.setValue(value ? value.substring(0, 20) : "");
            }
            setEnabled(this.unitControl, false);
        }
        else
        {
            this.unitControl.setValue(undefined);
            this.unitLgdTypeahead?.clearSearchText();
            setEnabled(this.unitControl, true);
        }
    }

    addStockLocation(): void
    {
        this.newStockLocation = !this.newStockLocation;
        if (this.newStockLocation)
        {
            const value = this.stockLocationLgdTypeahead?.searchText;
            this.selectedStockLocation = <LocationCodeViewModel>{};
            this.locationDescriptionControl.setValue(value ? value.substring(0, 50) : "");
            this.locationCodeControl.setValue(value ? value.substring(0, 20) : "");
            this.locationCodeControl.focus();
        }
        else
        {
            this.ClearStockLocation();
        }
    }

    lookupClasses($event: string): void
    {
        this.$itemClassLookupService.Lookup({ SearchText: $event }).then((results: Array<IClassCode>) =>
        {
            this.classSubject.next(results);
        });
    }

    lookupLocations($event: string): void
    {
        this.$inventoryLocationService
            .Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<LocationCodeViewModel>>) =>
            {
                this.locationSubject.next(executionResult.Result);
            });
    }

    lookupStockLocations($event: string): void
    {
        this.$inventoryLocationService
            .Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<LocationCodeViewModel>>) =>
            {
                this.stockLocationSubject.next(executionResult.Result);
            });
    }

    lookupUnits($event: string): void
    {
        this.$unitLookupService.Lookup({ SearchText: $event }).then((results: Array<IUnit>) =>
        {
            this.unitSubject.next(results);
        });
    }

    lookupVendors($event: string): void
    {
        this.$vendorLookupService
            .Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<IVendorLookup>>) =>
            {
                this.vendorSubject.next(executionResult.Result);
            });
    }

    private close(): void
    {
        this.dialogRef.close();
    }

    private save(): void
    {
        this.processing = true;
        const itemLocation = {
            Description: this._selectedLocation.Description,
            Code: this._selectedLocation.Code,
            Id: this._selectedLocation.Id,
            Status: this._locationStatus,
        } as IItemLocation;
        if (this.selectedStockLocation)
        {
            itemLocation.StockLocation = {
                Description: this.selectedStockLocation.Description,
                Code: this.selectedStockLocation.Code,
                Id: this.selectedStockLocation.Id,
            } as IItemLocation;
        }
        if (this.newStockLocation)
        {
            itemLocation.StockLocation = {
                Description: this.selectedStockLocation.Description,
                Code: this.selectedStockLocation.Code,
            } as IItemLocation;
        }
        this.item.Locations = [itemLocation];
        this.item.Vendors = [
            {
                VendorId: this.selectedVendor.Id,
                PartNumber: this.part,
                PurchasePrice: this.price || 0,
            } as IItemVendor,
        ];
        this.$itemService
            .SaveItem(this.item)
            .then(
                (result: IItem) =>
                {
                    this.dialogRef.close(result);
                },
                (error: string) =>
                {
                    this.$commonDialogsService.showAlertMessage(error, "An Error Occurred");
                }
            )
            .finally(() =>
            {
                this.processing = false;
            });
    }
}
